import React, { useEffect } from "react";
import { useRouteError, useNavigate } from "react-router-dom";
import { Refresh, Home, AlertTriangle } from "tabler-icons-react";
import mixpanel from "mixpanel-browser";

// ============================================
// PAGE D'ERREUR POUR REACT ROUTER
// ============================================
function ErrorFallback() {
    const error = useRouteError();
    const navigate = useNavigate();

    const handleRetry = () => {
        window.location.reload();
    };

    const handleGoHome = () => {
        navigate("/");
    };

    // Log l'erreur dans la console pour le debugging
    console.error("Route error:", error);

    // Remonter l'erreur dans Mixpanel : c'est le seul moyen de savoir ce qui
    // plante réellement chez les parents (message, stack, page, appareil).
    useEffect(() => {
        try {
            mixpanel.track("Erreur Application", {
                "Message": error?.message ?? String(error),
                "Stack": String(error?.stack ?? "").slice(0, 1000),
                "Page": window.location.pathname + window.location.search,
                "User Agent": navigator.userAgent,
            });
        } catch {}
    }, [error]);

    return (
        <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex flex-col items-center justify-center p-6">
            {/* Illustration */}
            <div className="w-32 h-32 bg-orange-100 rounded-full flex items-center justify-center mb-6 animate-pulse">
                <AlertTriangle size={64} className="text-orange-500" />
            </div>

            {/* Titre */}
            <h1 className="text-2xl font-bold text-gray-800 text-center mb-2">
                Oups ! Quelque chose s'est mal passé
            </h1>

            {/* Message rassurant */}
            <p className="text-gray-600 text-center max-w-sm mb-2">
                Ne vous inquiétez pas, ce n'est pas de votre faute. 
                L'application a rencontré un petit problème technique.
            </p>

            <p className="text-gray-500 text-sm text-center max-w-sm mb-8">
                Vos données sont en sécurité. Essayez de rafraîchir la page ou de retourner à l'accueil.
            </p>

            {/* Boutons d'action */}
            <div className="flex flex-col sm:flex-row gap-4 w-full max-w-sm">
                <button
                    onClick={handleRetry}
                    className="flex-1 bg-primary text-white px-6 py-4 rounded-full flex items-center justify-center gap-2 text-lg font-semibold hover:bg-primary/90 transition-colors shadow-lg"
                >
                    <Refresh size={24} />
                    Rafraîchir
                </button>

                <button
                    onClick={handleGoHome}
                    className="flex-1 bg-white text-gray-700 px-6 py-4 rounded-full flex items-center justify-center gap-2 text-lg font-semibold border-2 border-gray-200 hover:bg-gray-50 transition-colors"
                >
                    <Home size={24} />
                    Accueil
                </button>
            </div>

            {/* Info supplémentaire */}
            <div className="mt-10 bg-blue-50 border border-blue-200 rounded-xl p-4 max-w-sm">
                <p className="text-blue-700 text-sm text-center">
                    💡 <strong>Astuce :</strong> Si le problème persiste, essayez de fermer 
                    complètement l'application et de la rouvrir.
                </p>
            </div>

            {/* Détail technique (diagnostic terrain : vieux WebView, données
                malformées, etc.). Repliable pour ne pas effrayer le parent. */}
            {error ? (
                <details className="mt-4 max-w-sm w-full text-left">
                    <summary className="text-gray-400 text-xs text-center cursor-pointer">
                        Détails techniques
                    </summary>
                    <pre className="mt-2 p-3 bg-gray-100 rounded-lg text-[10px] text-gray-600 whitespace-pre-wrap break-all max-h-40 overflow-auto">
                        {String(error?.message ?? error)}
                        {"\n\n"}
                        {String(error?.stack ?? "").slice(0, 1500)}
                    </pre>
                </details>
            ) : null}

            {/* Footer */}
            <p className="mt-8 text-gray-400 text-xs text-center">
                Genimi - Votre partenaire éducatif
            </p>
        </div>
    );
}

export default ErrorFallback;

